/**
 * Entities — categories, brands and hashtags — as the data_points APIs return
 * them: an `entities` sidecar keyed by type then id, resolved against the
 * source the rows came from. See frontend/CONTRIBUTING.md, "Read the sidecar".
 */

import { SOURCES } from 'utils/dataSources';
import { shapeFlavors } from 'utils/helpers';

export const ENTITY_TYPES = {
    CATEGORY: 'category',
    BRAND: 'brand',
    HASHTAG: 'hashtag',
};

/** Sidecar keys are plural; row columns and `type` fields are singular. */
export const PLURAL_BY_ENTITY_TYPE = {
    category: 'categories',
    brand: 'brands',
    hashtag: 'hashtags',
};

/** Frontend SCREAMING_CASE to API snake_case; POPULARITY becomes `pop_index`. */
const API_DATA_SOURCE = Object.fromEntries(
    Object.entries(SOURCES).map(([key, { apiName }]) => [key, apiName]),
);

export const apiDataSource = (dataSource) =>
    API_DATA_SOURCE[String(dataSource).toUpperCase()] ||
    API_DATA_SOURCE.POPULARITY;

const DATA_SOURCE_KEY = Object.fromEntries(
    Object.entries(API_DATA_SOURCE).map(([key, apiName]) => [apiName, key]),
);

/**
 * The inverse, for reading a source name off an API payload. Undefined for a
 * name this frontend has no key for — callers decide what to show, rather than
 * being handed the pop index by default the way a request needs.
 */
export const dataSourceKey = (apiName) => DATA_SOURCE_KEY[apiName];

export const shapeTranslations = (googleTranslations, humanTranslations) => {
    const translations = [
        ...(googleTranslations || []),
        ...(humanTranslations || []),
    ];

    if (!translations.length) return null;

    const shapedTranslations = {};

    translations.forEach(({ destination_language, translation, type }) => {
        // prefer other translations to those from Google Translate
        if (!(
            shapedTranslations[destination_language] &&
            type === 'googletranslate'
        )) {
            shapedTranslations[destination_language] = translation;
        }
    });

    return shapedTranslations;
};

export const shapeEntity = ({
    human_translations,
    google_translations,
    minimal,
    ...entity
}) => ({
    ...entity,
    translations: shapeTranslations(google_translations, human_translations),
    minimal: !!minimal,
    flavors: shapeFlavors(entity.hierarchies),
});

export const shapeEntities = (entities) => entities.map(shapeEntity);

/**
 * Flatten an `entities` sidecar into one `{ [id]: shapedEntity }` lookup —
 * ids are unique across types, so row-shaping wants a single flat map.
 */
export const indexEntities = (entities) => {
    if (!entities) return {};
    return Object.values(entities).reduce((acc, byId) => {
        Object.values(byId || {}).forEach((entity) => {
            acc[entity.id] = shapeEntity(entity);
        });
        return acc;
    }, {});
};

/** Merge several sidecars into one, preserving the by-type then by-id shape. */
export const mergeEntities = (sidecars) =>
    (sidecars || []).reduce((acc, sidecar) => {
        Object.entries(sidecar || {}).forEach(([entityType, byId]) => {
            acc[entityType] = { ...acc[entityType], ...byId };
        });
        return acc;
    }, {});

/**
 * Query key for a single entity. Carries the data source because the same UUID
 * resolves to a different name and hierarchy depending on which source you ask.
 */
export const entityQueryKey = ({ id, dataSource, minimal = true }) => [
    'entity',
    { id, dataSource, minimal },
];

/**
 * Prime the cache from a sidecar so standalone entity lookups — breadcrumbs,
 * page headers, drawers — resolve without another request.
 */
export const seedEntityCache = (queryClient, entities, dataSource) => {
    if (!(queryClient && entities)) return;
    Object.values(entities).forEach((byId) => {
        Object.values(byId || {}).forEach((entity) => {
            queryClient.setQueryData(
                entityQueryKey({ id: entity.id, dataSource }),
                shapeEntity(entity),
            );
        });
    });
};
